import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { Alert, TextInputField } from 'ui';
import { AlertIcon } from 'ui/icons';

const validationSchema = yup.object({
  bank_name: yup.string().trim().required('This field is required'),
  bank_account_bsb: yup.string().trim().required('This field is required'),
  bank_account_name: yup.string().trim().required('This field is required'),
  bank_account_number: yup.string().trim().required('This field is required'),
});

const initialValues = {
  bank_name: '',
  bank_account_bsb: '',
  bank_account_name: '',
  bank_account_number: '',
};

export const PaymentInstructions = ({ moveForward, allowSkip }: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const getInitialValues = () => {
    return {
      ...initialValues,
      ...data?.entity,
    };
  };

  const formik = useFormik({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: getInitialValues(),
    onSubmit: async values => {
      if (formik.dirty) {
        await updateEntity({
          ...data?.entity,
          ...values,
        });
      }
      moveForward && moveForward();
    },
    enableReinitialize: true,
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const handleOnSkip = () => {
    formik.setErrors({});
    if (!formik.values.bank_name) {
      formik.setFieldValue('bank_name', 'Unknown');
    }
    if (!formik.values.bank_account_bsb) {
      formik.setFieldValue('bank_account_bsb', 'Unknown');
    }
    if (!formik.values.bank_account_name) {
      formik.setFieldValue('bank_account_name', 'Unknown');
    }
    if (!formik.values.bank_account_number) {
      formik.setFieldValue('bank_account_number', 'Unknown');
    }
    setTimeout(() => {
      formik.handleSubmit();
    }, 1);
  };

  return (
    <OnboardingWrapper
      title="Payment instructions"
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? handleOnSkip : undefined}
      formik={formik}
      isLoading={isLoading}
    >
      <>
        <div className="w-full flex-col items-start space-y-2 mb-6">
          <p className="text-[16px] leading-[24px]">
            Please provide your EFT details below for all cash payments
          </p>
          <p className="text-[14px] leading-[22px] text-gray-600">
            Payments will only be made electronically to Australian bank accounts. We will not make
            any payments into third party bank accounts.
          </p>
          <p className="text-[14px] leading-[22px] text-gray-600">
            Investors who do not provide EFT details will have distribution payments defaulted into
            reinvestment (where eligible).
          </p>
        </div>
        <div className="w-full flex-col items-start space-y-8">
          <Alert
            icon={AlertIcon}
            variant="neutral-2"
            label="Distribution payments will be made by EFT to investors."
          />
          <TextInputField label="Bank name" name="bank_name" />
          <TextInputField label="bsb" name="bank_account_bsb" placeholder="123-123" />
          <TextInputField label="account number" name="bank_account_number" />
          <TextInputField label="account name" name="bank_account_name" />
        </div>
      </>
    </OnboardingWrapper>
  );
};
